import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FieldError } from '../../../../../components/novarium/NvTableParts';
import useNotify from '../../../../../hooks/useNotify';
import { parseApiError } from '../../../../../services/apiError';
import { formatMoney } from '../../../../../utils/money';
import {
  createInvoice, getIibbAliquot, getInvoicePreview, getInvoicingStatus, getPosForOrder,
} from '../../../services/AgendaService';
import { InvoicePreview, InvoicePreviewRequest, InvoicingStatusRow, SelectOption } from '../../../types/billing.types';
import { errorMessageOf, FieldErrors, fieldErrorsOf, fromApiDate, toApiDate } from '../billingUtils';

/** Cómo terminó el paso: se facturó (y si AFIP ya la numeró), o se omitió y la orden quedó pendiente. */
export type InvoiceStepResult =
  | { kind: 'invoiced'; invoiceId: number; formType: string; payable: boolean }
  | { kind: 'skipped' };

interface InvoiceStepProps {
  orderId: number;
  exitRef?: MutableRefObject<(() => void) | null>;
  onClose: (result: InvoiceStepResult) => void;
}

const AFIP_POLL_MS = 3000;
const AFIP_MAX_ATTEMPTS = 20;
const NO_VAT = -1;

interface CustomerForm {
  vatCategoryId: number | null;
  documentTypeId: number | null;
  document: string;
  businessName: string;
  address: string;
}

/**
 * Paso "Factura" del cobro del turno, con los campos de la ventana de factura de Vaadin ({@code AdminFormInvoiceBaseWindow}):
 * datos fiscales del cliente, tipo y punto de venta, medio de pago, vencimiento de la FCE, IVA de cada ítem y
 * percepción de IIBB. Los montos los calcula el back cada vez que cambia algo; si la numera AFIP, se espera el
 * resultado en el mismo paso.
 */
export default function InvoiceStep({ orderId, exitRef, onClose }: InvoiceStepProps) {
  const notify = useNotify();
  const [step, setStep] = useState<'loading' | 'load-error' | 'form' | 'afip'>('loading');
  const [preview, setPreview] = useState<InvoicePreview | null>(null);
  const [customer, setCustomer] = useState<CustomerForm>({ vatCategoryId: null, documentTypeId: null, document: '', businessName: '', address: '' });
  const [formTypeId, setFormTypeId] = useState<number | null>(null);
  const [posOptions, setPosOptions] = useState<SelectOption[]>([]);
  const [posId, setPosId] = useState<number | null>(null);
  const [paymentMethodId, setPaymentMethodId] = useState<number | null>(null);
  const [bankAccount, setBankAccount] = useState('');
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [iibbPercentage, setIibbPercentage] = useState<number | null>(null);
  const [itemTaxTypeIds, setItemTaxTypeIds] = useState<(number | null)[]>([]);
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchingAliquot, setSearchingAliquot] = useState(false);

  const [invoice, setInvoice] = useState<{ id: number; formType: string } | null>(null);
  const [afipRows, setAfipRows] = useState<InvoicingStatusRow[]>([]);
  const [afipState, setAfipState] = useState<'running' | 'ok' | 'error' | 'timeout'>('running');
  const sequence = useRef(0);

  /** Pide los montos con lo elegido; el back también devuelve el tipo por defecto si el elegido ya no corresponde. */
  const refresh = useCallback(async (request: InvoicePreviewRequest, initial = false) => {
    const current = ++sequence.current;
    setRefreshing(true);
    try {
      const data = await getInvoicePreview(orderId, request);
      if (current !== sequence.current) return;
      setPreview(data);
      setFormTypeId(data.defaultFormTypeId);
      setItemTaxTypeIds(data.items.map((item) => item.taxTypeId));
      setIibbPercentage(data.iibbPercentage);
      if (initial) {
        setCustomer({
          vatCategoryId: data.customer.vatCategoryId,
          documentTypeId: data.customer.documentTypeId,
          document: data.customer.document ?? '',
          businessName: data.customer.businessName ?? '',
          address: data.customer.address ?? '',
        });
        setPaymentMethodId(data.defaultPaymentMethodId);
        setBankAccount(data.bankAccount ?? '');
        setStep('form');
      }
    } catch (error: unknown) {
      if (current !== sequence.current) return;
      const apiError = parseApiError(error);
      if (initial) {
        notify.error(errorMessageOf(apiError, 'No se pudieron cargar los datos de la factura. Intentá de nuevo.'));
        setStep('load-error');
        return;
      }
      const fieldErrors = fieldErrorsOf(apiError);
      if (Object.keys(fieldErrors).length > 0) setErrors((e) => ({ ...e, ...fieldErrors }));
      else notify.error(errorMessageOf(apiError, 'No se pudieron calcular los montos de la factura. Intentá de nuevo.'));
    } finally {
      if (current === sequence.current) setRefreshing(false);
    }
  }, [orderId, notify]);

  useEffect(() => { void refresh({}, true); }, [refresh]);

  const request = (patch: Partial<InvoicePreviewRequest> = {}): InvoicePreviewRequest => ({
    vatCategoryId: customer.vatCategoryId,
    formTypeId,
    posId,
    iibbPercentage,
    itemTaxTypeIds,
    expiryDate: expiryDate ? toApiDate(expiryDate) : null,
    ...patch,
  });

  // Puntos de venta del emisor para el tipo elegido; se preselecciona el del cliente
  useEffect(() => {
    if (step !== 'form' || !formTypeId) {
      setPosOptions([]);
      setPosId(null);
      return undefined;
    }
    let cancelled = false;
    getPosForOrder(orderId, formTypeId)
      .then((options) => {
        if (cancelled) return;
        setPosOptions(options);
        setPosId((current) => (current && options.some((o) => o.id === current) ? current
          : options.find((o) => o.isDefault)?.id ?? options[0]?.id ?? null));
      })
      .catch((error: unknown) => {
        parseApiError(error);
        if (!cancelled) setPosOptions([]);
      });
    return () => { cancelled = true; };
  }, [orderId, formTypeId, step]);

  const clearError = (field: string) => setErrors((current) => {
    if (!current[field]) return current;
    const next = { ...current };
    delete next[field];
    return next;
  });

  const selectedType = preview?.formTypes.find((t) => t.id === formTypeId) ?? null;
  const showIibb = Boolean(preview?.iibbApplies && selectedType?.taxes);
  const isBankDebit = paymentMethodId != null && paymentMethodId === preview?.bankDebitPaymentMethodId;
  const shownExpiry = expiryDate ?? fromApiDate(preview?.defaultExpiryDate);

  /** Como {@code checkIfShouldEmit}: si ARCA dice que tiene que ser FCE (o que no), se marca el tipo en el momento. */
  const fceProblem = (() => {
    if (!preview || !selectedType || !selectedType.fceChecked || preview.shouldEmitFce == null) return undefined;
    if (preview.shouldEmitFce && !selectedType.fce) {
      return `El cliente está habilitado para FCE: elegí una factura FCE${preview.fceMinAmount != null ? ` (monto mínimo ${formatMoney(preview.fceMinAmount)})` : ''}`;
    }
    if (!preview.shouldEmitFce && selectedType.fce) return 'Este cliente o este monto no corresponden a una FCE: elegí la factura común';
    return undefined;
  })();

  const changeVatCategory = (vatCategoryId: number | null) => {
    setCustomer((c) => ({ ...c, vatCategoryId }));
    clearError('customer.vatCategoryId');
    if (vatCategoryId) void refresh(request({ vatCategoryId, formTypeId: null }));
  };

  const changeFormType = (id: number | null) => {
    setFormTypeId(id);
    clearError('formTypeId');
    if (id) void refresh(request({ formTypeId: id }));
  };

  const changePos = (id: number | null) => {
    setPosId(id);
    clearError('posId');
    void refresh(request({ posId: id }));
  };

  const changeItemTax = (index: number, value: number) => {
    const next = itemTaxTypeIds.map((id, i) => (i === index ? (value === NO_VAT ? null : value) : id));
    setItemTaxTypeIds(next);
    void refresh(request({ itemTaxTypeIds: next }));
  };

  async function searchAliquot() {
    if (!customer.document.trim()) {
      setErrors((e) => ({ ...e, 'customer.document': 'Completá el documento para buscar la alícuota' }));
      return;
    }
    setSearchingAliquot(true);
    try {
      const percentage = await getIibbAliquot(customer.document.trim());
      setIibbPercentage(percentage);
      await refresh(request({ iibbPercentage: percentage }));
    } catch (error: unknown) {
      notify.error(errorMessageOf(parseApiError(error), 'No se pudo buscar la alícuota. Intentá de nuevo.'));
    } finally {
      setSearchingAliquot(false);
    }
  }

  function validate(): FieldErrors {
    const result: FieldErrors = {};
    if (!customer.vatCategoryId) result['customer.vatCategoryId'] = 'Elegí la condición de IVA';
    if (!customer.businessName.trim()) result['customer.businessName'] = 'Completá el nombre o la razón social';
    if (!formTypeId) result.formTypeId = 'Elegí el tipo de comprobante';
    else if (fceProblem) result.formTypeId = fceProblem;
    if (!posId) result.posId = posOptions.length === 0 ? 'El emisor no tiene puntos de venta para este comprobante' : 'Elegí el punto de venta';
    if (!paymentMethodId) result.paymentMethodId = 'Elegí el medio de pago';
    if (isBankDebit && !bankAccount.trim()) result.bankAccount = 'Ingresá el número de cuenta bancaria';
    return result;
  }

  async function handleInvoice() {
    const validation = validate();
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;
    setSaving(true);
    try {
      const result = await createInvoice(orderId, {
        formTypeId,
        posId,
        paymentMethodId,
        description: description.trim() || undefined,
        iibbPercentage: showIibb ? iibbPercentage ?? 0 : null,
        itemTaxTypeIds: selectedType?.taxes ? itemTaxTypeIds : [],
        expiryDate: selectedType?.fce && shownExpiry ? toApiDate(shownExpiry) : null,
        bankAccount: isBankDebit ? bankAccount.trim() : null,
        customer: {
          vatCategoryId: customer.vatCategoryId,
          documentTypeId: customer.documentTypeId,
          document: customer.document.trim(),
          businessName: customer.businessName.trim(),
          address: customer.address.trim(),
        },
      });
      if (result.externalNumber && result.invoicingLogId) {
        setInvoice({ id: result.invoiceId, formType: result.formType });
        setStep('afip');
        pollAfip(result.invoicingLogId);
      } else {
        notify.success('Factura registrada');
        onClose({ kind: 'invoiced', invoiceId: result.invoiceId, formType: result.formType, payable: true });
      }
    } catch (error: unknown) {
      const apiError = parseApiError(error);
      const fieldErrors = fieldErrorsOf(apiError);
      if (Object.keys(fieldErrors).length > 0) setErrors(fieldErrors);
      else notify.error(errorMessageOf(apiError, 'No se pudo registrar la factura. Intentá de nuevo.'));
    } finally {
      setSaving(false);
    }
  }

  const pollTimer = useRef<number | null>(null);
  useEffect(() => () => { if (pollTimer.current) window.clearTimeout(pollTimer.current); }, []);

  function pollAfip(logId: number, attempt = 1) {
    getInvoicingStatus(logId)
      .then((status) => {
        setAfipRows(status.rows ?? []);
        if (status.finished) {
          setAfipState(status.status === 3 && !(status.rows ?? []).some((row) => row.error) ? 'ok' : 'error');
          return;
        }
        scheduleNext();
      })
      .catch((error: unknown) => {
        parseApiError(error);
        scheduleNext();
      });

    function scheduleNext() {
      if (attempt >= AFIP_MAX_ATTEMPTS) {
        setAfipState('timeout');
        return;
      }
      pollTimer.current = window.setTimeout(() => pollAfip(logId, attempt + 1), AFIP_POLL_MS);
    }
  }

  const closeAfterAfip = useCallback(() => {
    if (!invoice) return;
    onClose({ kind: 'invoiced', invoiceId: invoice.id, formType: invoice.formType, payable: afipState === 'ok' });
  }, [invoice, afipState, onClose]);

  const skip = useCallback(() => onClose({ kind: 'skipped' }), [onClose]);

  const leave = useCallback(() => {
    if (step === 'afip') {
      if (afipState !== 'running') closeAfterAfip();
      return;
    }
    if (step !== 'form') {
      skip();
      return;
    }
    confirmDialog({
      header: '¿Salir sin facturar?',
      message: 'La orden queda pendiente de facturar; la podés facturar desde el turno.',
      className: 'nv-scope nv-confirm',
      footer: (options) => (
        <>
          <Button type="button" link label="Seguir facturando" className="nv-push-left" onClick={options.reject} />
          <Button type="button" className="buttonMain2" outlined label="Salir sin facturar" onClick={options.accept} />
        </>
      ),
      accept: skip,
    });
  }, [step, afipState, closeAfterAfip, skip]);

  useEffect(() => {
    if (exitRef) exitRef.current = leave;
  }, [exitRef, leave]);

  if (step === 'loading') {
    return <div className="nv-loading" role="status"><ProgressSpinner style={{ width: 24, height: 24 }} strokeWidth="4" /> Cargando…</div>;
  }

  if (step === 'load-error' || !preview) {
    return (
      <>
        <p className="nv-note"><i className="pi pi-info-circle" aria-hidden /> No se pudo armar la factura. La orden quedó pendiente de facturar: la podés facturar desde el turno.</p>
        <footer className="nv-page-foot">
          <hr className="nv-divider" />
          <div className="nv-actions-end">
            <Button type="button" className="buttonMain" label="Cerrar" onClick={skip} />
          </div>
        </footer>
      </>
    );
  }

  if (step === 'afip') {
    return (
      <>
        <section className="nv-section" aria-labelledby="afip-title" aria-live="polite">
          <h2 id="afip-title" className="nv-section-title">Proceso de autorización AFIP</h2>
          <div className="nv-status-row">
            <span className="nv-strong">{invoice?.formType}</span>
            {afipState === 'running' && <span className="nv-loading"><ProgressSpinner style={{ width: 16, height: 16 }} strokeWidth="4" /> Registrando en AFIP…</span>}
            {afipState === 'ok' && <span className="nv-status-ok"><i className="pi pi-check-circle" aria-hidden /> Autorizada por AFIP</span>}
            {afipState === 'error' && <span className="nv-status-err"><i className="pi pi-times-circle" aria-hidden /> AFIP no la autorizó</span>}
            {afipState === 'timeout' && <span className="nv-status-warn"><i className="pi pi-clock" aria-hidden /> AFIP todavía no respondió</span>}
          </div>
          <ul className="nv-stack-4" aria-label="Detalle del registro en AFIP">
            {afipRows.map((row, index) => (
              <li key={index} className="nv-row-8">
                <i className={`pi ${row.error ? 'pi-times nv-status-err' : row.ok ? 'pi-check nv-status-ok' : 'pi-info-circle nv-muted'}`} aria-hidden />
                {/* El texto viene del proceso de AFIP: se muestra como texto, nunca como HTML */}
                <span>{row.message}</span>
              </li>
            ))}
          </ul>
          {(afipState === 'error' || afipState === 'timeout') && (
            <p className="nv-note"><i className="pi pi-info-circle" aria-hidden /> La factura quedó registrada sin número de AFIP. Cuando se autorice, cobrala desde el turno.</p>
          )}
        </section>
        <footer className="nv-page-foot">
          <hr className="nv-divider" />
          <div className="nv-actions-end">
            {afipState === 'ok' ? (
              <Button type="button" className="buttonMain" label="Cobrar" icon="pi pi-dollar" onClick={closeAfterAfip} />
            ) : (
              <Button
                type="button"
                className="buttonMain"
                label="Cerrar"
                onClick={closeAfterAfip}
                disabled={afipState === 'running'}
                tooltip={afipState === 'running' ? 'Esperá a que AFIP termine de autorizar la factura' : undefined}
                tooltipOptions={{ showOnDisabled: true, event: 'both' }}
              />
            )}
          </div>
        </footer>
      </>
    );
  }

  const vatOptions = [{ id: NO_VAT, name: 'Sin IVA' }, ...preview.vatTypes];
  type Item = InvoicePreview['items'][number];

  return (
    <>
      {preview.inGuarantee && (
        <p className="nv-note"><i className="pi pi-info-circle" aria-hidden /> Orden en garantía: la factura sale marcada en garantía.</p>
      )}

      <section className="nv-section" aria-labelledby="invoice-customer-title">
        <h2 id="invoice-customer-title" className="nv-section-title">Cliente</h2>
        <div className="nv-form-row">
          <div className="nv-field nv-w-m">
            <label htmlFor="invoice-vat">Cond. IVA <span className="nv-req">*</span></label>
            <Dropdown panelClassName="nv-scope" inputId="invoice-vat" value={customer.vatCategoryId} options={preview.vatCategories} optionLabel="name" optionValue="id"
              placeholder="Seleccionar" onChange={(e) => changeVatCategory(e.value ?? null)}
              className={errors['customer.vatCategoryId'] ? 'p-invalid' : undefined} />
            <FieldError id="invoice-vat-error" message={errors['customer.vatCategoryId']} />
          </div>
          <div className="nv-field nv-w-s">
            <label htmlFor="invoice-doc-type">Tipo de doc.</label>
            <Dropdown panelClassName="nv-scope" inputId="invoice-doc-type" value={customer.documentTypeId} options={preview.documentTypes} optionLabel="name" optionValue="id"
              placeholder="Seleccionar" showClear
              onChange={(e) => { setCustomer((c) => ({ ...c, documentTypeId: e.value ?? null })); clearError('customer.documentTypeId'); }}
              className={errors['customer.documentTypeId'] ? 'p-invalid' : undefined} />
            <FieldError id="invoice-doc-type-error" message={errors['customer.documentTypeId']} />
          </div>
          <div className="nv-field nv-w-s">
            <label htmlFor="invoice-doc">ID fiscal</label>
            <InputText id="invoice-doc" value={customer.document} maxLength={50}
              onChange={(e) => { setCustomer((c) => ({ ...c, document: e.target.value })); clearError('customer.document'); }}
              className={errors['customer.document'] ? 'p-invalid' : undefined}
              aria-invalid={Boolean(errors['customer.document'])} aria-describedby={errors['customer.document'] ? 'invoice-doc-error' : undefined} />
            <FieldError id="invoice-doc-error" message={errors['customer.document']} />
          </div>
        </div>
        <div className="nv-form-row">
          <div className="nv-field nv-w-m">
            <label htmlFor="invoice-name">Cliente <span className="nv-req">*</span></label>
            <InputText id="invoice-name" value={customer.businessName} maxLength={255}
              onChange={(e) => { setCustomer((c) => ({ ...c, businessName: e.target.value })); clearError('customer.businessName'); }}
              className={errors['customer.businessName'] ? 'p-invalid' : undefined}
              aria-invalid={Boolean(errors['customer.businessName'])} aria-describedby={errors['customer.businessName'] ? 'invoice-name-error' : undefined} />
            <FieldError id="invoice-name-error" message={errors['customer.businessName']} />
          </div>
          <div className="nv-field nv-w-l">
            <label htmlFor="invoice-address">Domicilio</label>
            <InputText id="invoice-address" value={customer.address} maxLength={255} placeholder="Ej.: Av. Colón 1234, Mar del Plata"
              onChange={(e) => setCustomer((c) => ({ ...c, address: e.target.value }))} />
          </div>
        </div>
      </section>

      <section className="nv-section" aria-labelledby="invoice-form-title">
        <h2 id="invoice-form-title" className="nv-section-title">Comprobante · {preview.billingEntity.name}</h2>
        <div className="nv-form-row">
          <div className="nv-field nv-w-m">
            <label htmlFor="invoice-type">Tipo de comprobante <span className="nv-req">*</span></label>
            <Dropdown panelClassName="nv-scope" inputId="invoice-type" value={formTypeId} options={preview.formTypes} optionLabel="name" optionValue="id"
              placeholder={preview.formTypes.length === 0 ? 'No hay tipos para esta condición de IVA' : 'Seleccionar'}
              onChange={(e) => changeFormType(e.value ?? null)}
              className={errors.formTypeId || fceProblem ? 'p-invalid' : undefined} />
            <FieldError id="invoice-type-error" message={errors.formTypeId ?? fceProblem} />
          </div>
          <div className="nv-field nv-w-m">
            <label htmlFor="invoice-pos">Punto de venta <span className="nv-req">*</span></label>
            <Dropdown panelClassName="nv-scope" inputId="invoice-pos" value={posId} options={posOptions} optionLabel="name" optionValue="id"
              placeholder={formTypeId ? 'Seleccionar' : 'Elegí el tipo de comprobante primero'} disabled={!formTypeId}
              onChange={(e) => changePos(e.value ?? null)} className={errors.posId ? 'p-invalid' : undefined} />
            <FieldError id="invoice-pos-error" message={errors.posId} />
          </div>
          <div className="nv-field nv-w-l">
            <label htmlFor="invoice-description">Descripción</label>
            <InputText id="invoice-description" value={description} maxLength={255} placeholder="Ej.: Consulta del 21/10"
              onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>
        <div className="nv-form-row">
          <div className="nv-field nv-w-m">
            <label htmlFor="invoice-payment-method">Medio de pago <span className="nv-req">*</span></label>
            <Dropdown panelClassName="nv-scope" inputId="invoice-payment-method" value={paymentMethodId} options={preview.paymentMethods} optionLabel="name" optionValue="id"
              placeholder="Seleccionar" onChange={(e) => { setPaymentMethodId(e.value ?? null); clearError('paymentMethodId'); clearError('bankAccount'); }}
              className={errors.paymentMethodId ? 'p-invalid' : undefined} />
            <FieldError id="invoice-payment-method-error" message={errors.paymentMethodId} />
          </div>
          {isBankDebit && (
            <div className="nv-field nv-w-m">
              <label htmlFor="invoice-bank-account">Cuenta bancaria <span className="nv-req">*</span></label>
              <InputText id="invoice-bank-account" value={bankAccount} maxLength={255}
                onChange={(e) => { setBankAccount(e.target.value); clearError('bankAccount'); }}
                className={errors.bankAccount ? 'p-invalid' : undefined} />
              <FieldError id="invoice-bank-account-error" message={errors.bankAccount} />
            </div>
          )}
          {selectedType?.fce && (
            <div className="nv-field nv-w-s">
              <label htmlFor="invoice-expiry">Fecha de vencimiento</label>
              <Calendar panelClassName="nv-scope" inputId="invoice-expiry" value={shownExpiry} minDate={new Date()} dateFormat="dd/mm/yy" showIcon
                onChange={(e) => { setExpiryDate((e.value as Date) ?? null); clearError('expiryDate'); }}
                className={errors.expiryDate ? 'p-invalid' : undefined} />
              <FieldError id="invoice-expiry-error" message={errors.expiryDate} />
            </div>
          )}
        </div>
      </section>

      <section className="nv-section" aria-labelledby="invoice-items-title">
        <h2 id="invoice-items-title" className="nv-section-title">Items</h2>
        <DataTable value={preview.items.map((item, index) => ({ ...item, index }))} dataKey="index" className="nv-load-table"
          emptyMessage="La orden no tiene ítems para facturar.">
          <Column header="Item" body={(row: Item) => row.description} className="nv-col-item" />
          <Column header="Precio unit." body={(row: Item) => formatMoney(row.unitPrice)} align="right" alignHeader="right" className="nv-col-money" />
          <Column header="Cantidad" field="quantity" align="center" alignHeader="center" className="nv-col-qty" />
          <Column header="Subtotal" body={(row: Item) => formatMoney(row.subtotal)} align="right" alignHeader="right" className="nv-col-money" />
          <Column
            header="IVA"
            className="nv-col-status"
            body={(row: Item & { index: number }) => (selectedType?.taxes ? (
              <Dropdown panelClassName="nv-scope"
                value={itemTaxTypeIds[row.index] ?? NO_VAT}
                options={vatOptions}
                optionLabel="name"
                optionValue="id"
                onChange={(e) => changeItemTax(row.index, e.value)}
                disabled={refreshing}
                className="nv-in-cell"
                aria-label={`IVA de ${row.description}`}
              />
            ) : '—')}
          />
          <Column header="Imp. IVA" body={(row: Item) => formatMoney(row.vatAmount)} align="right" alignHeader="right" className="nv-col-money" />
          <Column header="Total" body={(row: Item) => formatMoney(row.total)} align="right" alignHeader="right" className="nv-col-money" />
        </DataTable>

        {showIibb && (
          <div className="nv-form-row">
            <div className="nv-field nv-w-s">
              <label htmlFor="invoice-iibb">% Per. IIBB</label>
              <InputNumber inputId="invoice-iibb" value={iibbPercentage} mode="decimal" locale="es-AR" minFractionDigits={2} maxFractionDigits={2}
                min={0} max={100} onValueChange={(e) => { setIibbPercentage(e.value ?? 0); clearError('iibbPercentage'); }}
                onBlur={() => void refresh(request())} className={errors.iibbPercentage ? 'p-invalid' : undefined} />
              <FieldError id="invoice-iibb-error" message={errors.iibbPercentage} />
            </div>
            <div className="nv-field-inline-action">
              <Button type="button" className="buttonMain3" label="Buscar alícuota en el padrón" onClick={searchAliquot} loading={searchingAliquot} />
            </div>
          </div>
        )}

        <dl className="nv-form-row" aria-busy={refreshing}>
          {[
            ['Neto gravado', preview.amounts.netTaxed],
            ['Exento', preview.amounts.exempt],
            ['No gravado', preview.amounts.notTaxed],
            ['IVA', preview.amounts.vat],
            ['Per. IIBB', preview.amounts.iibb],
          ].map(([label, value]) => (
            <div key={label as string} className="nv-ro nv-w-s">
              <dt className="nv-ro-label">{label}</dt>
              <dd className="nv-ro-value">{formatMoney(value as number)}</dd>
            </div>
          ))}
          <div className="nv-ro nv-w-s">
            <dt className="nv-ro-label">Total</dt>
            <dd className="nv-ro-value nv-strong">{formatMoney(preview.amounts.total)}</dd>
          </div>
        </dl>
      </section>

      <footer className="nv-page-foot">
        <hr className="nv-divider" />
        <div className="nv-actions-end">
          <Button type="button" className="buttonMain2" outlined label="Omitir factura" onClick={skip} disabled={saving} />
          <Button type="button" className="buttonMain" label="Facturar" onClick={handleInvoice} loading={saving} />
        </div>
      </footer>
    </>
  );
}
